/*
 * Copyright (C) 2021 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import {formatDatetimeJoin, lookupDatetime} from '@canvas/i18next/localizeDate'
import {loadI18nFormats} from '@instructure/moment-utils'

type FormatFn = () => string | null

const dateFormat =
  (key: string): FormatFn =>
  () =>
    lookupDatetime(`date.formats.${key}`)

const eventFormat =
  ({date, time}: {date: string; time: string}): FormatFn =>
  () =>
    formatDatetimeJoin('time.event', {
      date: lookupDatetime(`date.formats.${date}`) ?? '',
      time: lookupDatetime(`time.formats.${time}`) ?? '',
    })

const timeFormat =
  (key: string): FormatFn =>
  () =>
    lookupDatetime(`time.formats.${key}`)

const joinFormats =
  (separator: string, formats: string[]): FormatFn =>
  () =>
    formats.map(key => lookupDatetime(key)).join(separator)

export function prepareFormats(): FormatFn[] {
  // examples are from en_US. order is significant since if an input matches
  // multiple formats, the format earlier in the list will be preferred
  return [
    timeFormat('default'), // %a, %d %b %Y %H:%M:%S %z
    dateFormat('full_with_weekday'), // %a %b %-d, %Y %-l:%M%P
    dateFormat('full'), // %b %-d, %Y %-l:%M%P
    dateFormat('date_at_time'), // %b %-d at %l:%M%P
    dateFormat('long_with_weekday'), // %A, %B %-d
    dateFormat('medium_with_weekday'), // %a %b %-d, %Y
    dateFormat('short_with_weekday'), // %a, %b %-d
    timeFormat('long'), // %B %d, %Y %H:%M
    dateFormat('long'), // %B %-d, %Y
    eventFormat({date: 'medium', time: 'tiny'}),
    eventFormat({date: 'medium', time: 'tiny_on_the_hour'}),
    eventFormat({date: 'short', time: 'tiny'}),
    eventFormat({date: 'short', time: 'tiny_on_the_hour'}),
    joinFormats(' ', ['date.formats.medium', 'time.formats.tiny']),
    joinFormats(' ', ['date.formats.medium', 'time.formats.tiny_on_the_hour']),
    dateFormat('medium'), // %b %-d, %Y
    timeFormat('short'), // %d %b %H:%M
    joinFormats(' ', ['date.formats.short', 'time.formats.tiny']),
    joinFormats(' ', ['date.formats.short', 'time.formats.tiny_on_the_hour']),
    dateFormat('short'), // %b %-d
    dateFormat('default'), // %Y-%m-%d
    timeFormat('tiny'), // %l:%M%P
    timeFormat('tiny_on_the_hour'), // %l%P
    dateFormat('weekday'), // %A
    dateFormat('short_weekday'), // %a
  ]
}

export function up(): void {
  loadI18nFormats(prepareFormats())
}

export function down(): void {
  loadI18nFormats([])
}
